import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  Select,
  SelectOption,
  Spinner,
  Textarea,
} from '../../components/ui';

interface TenantEmailStatusResponse {
  has_platform_email: boolean;
  is_platform_enabled: boolean;
  is_platform_healthy: boolean;
  provider_type: string | null;
  effective_from_email: string | null;
  effective_from_name: string | null;
  verified_sender_domains: string[];
}

interface TenantEmailSettingsResponse {
  from_name: string | null;
  from_email: string | null;
  reply_to: string | null;
  company_name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  email_footer_text: string | null;
  include_powered_by: boolean;
}

interface TenantSendLogEntry {
  id: number;
  purpose: string;
  to_email: string;
  subject: string;
  status: string;
  error_message: string | null;
  queued_at: string;
  sent_at: string | null;
  delivered_at: string | null;
}

interface TenantSendLogResponse {
  items: TenantSendLogEntry[];
  total: number;
}

interface SettingsFormState {
  from_name: string;
  from_email: string;
  reply_to: string;
  company_name: string;
  logo_url: string;
  primary_color: string;
  email_footer_text: string;
  include_powered_by: boolean;
}

const INITIAL_FORM: SettingsFormState = {
  from_name: '',
  from_email: '',
  reply_to: '',
  company_name: '',
  logo_url: '',
  primary_color: '#c9506b',
  email_footer_text: '',
  include_powered_by: true,
};

function getApiErrorMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item: any) => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
      .join(' | ');
  }
  return fallback;
}

export default function EmailSettingsPage() {
  const { push } = useToasts();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshingLogs, setIsRefreshingLogs] = useState(false);

  const [status, setStatus] = useState<TenantEmailStatusResponse | null>(null);
  const [form, setForm] = useState<SettingsFormState>(INITIAL_FORM);
  const [logs, setLogs] = useState<TenantSendLogEntry[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [resendingLogId, setResendingLogId] = useState<number | null>(null);

  const setField = <K extends keyof SettingsFormState>(key: K, value: SettingsFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadStatus = async () => {
    const response = await AXIOS_INSTANCE.get<TenantEmailStatusResponse>('/api/v1/admin/email/status');
    setStatus(response.data);
  };

  const loadSettings = async () => {
    const response = await AXIOS_INSTANCE.get<TenantEmailSettingsResponse>('/api/v1/admin/email/settings');
    const settings = response.data;
    setForm({
      from_name: settings.from_name || '',
      from_email: settings.from_email || '',
      reply_to: settings.reply_to || '',
      company_name: settings.company_name || '',
      logo_url: settings.logo_url || '',
      primary_color: settings.primary_color || '#c9506b',
      email_footer_text: settings.email_footer_text || '',
      include_powered_by: settings.include_powered_by,
    });
  };

  const loadLogs = async () => {
    const params: Record<string, string | number> = { page: 1, page_size: 20 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (purposeFilter !== 'all') params.purpose = purposeFilter;
    const response = await AXIOS_INSTANCE.get<TenantSendLogResponse>('/api/v1/admin/email/send-log', {
      params,
    });
    setLogs(response.data.items || []);
    setTotalLogs(response.data.total || 0);
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadStatus(), loadSettings(), loadLogs()]);
      } catch (error: any) {
        push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to load tenant email settings') });
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const refreshLogs = async () => {
      setIsRefreshingLogs(true);
      try {
        await loadLogs();
      } catch (error: any) {
        push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to refresh email logs') });
      } finally {
        setIsRefreshingLogs(false);
      }
    };
    if (!isLoading) {
      refreshLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, purposeFilter]);

  const handleResend = async (logId: number) => {
    setResendingLogId(logId);
    try {
      await AXIOS_INSTANCE.post(`/api/v1/admin/email/send-log/${logId}/resend`);
      push({ kind: 'success', message: 'Resend queued' });
      await loadLogs();
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to queue resend') });
    } finally {
      setResendingLogId(null);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await AXIOS_INSTANCE.put('/api/v1/admin/email/settings', {
        from_name: form.from_name.trim() || null,
        from_email: form.from_email.trim() || null,
        reply_to: form.reply_to.trim() || null,
        company_name: form.company_name.trim() || null,
        logo_url: form.logo_url.trim() || null,
        primary_color: form.primary_color.trim() || null,
        email_footer_text: form.email_footer_text.trim() || null,
        include_powered_by: form.include_powered_by,
      });
      push({ kind: 'success', message: 'Tenant email branding settings saved' });
      await Promise.all([loadStatus(), loadSettings()]);
    } catch (error: any) {
      push({ kind: 'error', message: getApiErrorMessage(error, 'Failed to save tenant email settings') });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Page maxWidth="xl">
        <PageBody>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" />
            Loading tenant email settings...
          </div>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page maxWidth="xl">
      <PageHeader
        title="Email Settings"
        description="Consume platform email delivery and configure tenant-level sender branding."
        actions={
          <Button onClick={handleSave} disabled={isSaving || !status?.has_platform_email}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            Save Branding Settings
          </Button>
        }
      />
      <PageBody className="space-y-6 pb-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-eliza-red" />
              Platform Email Connection
            </CardTitle>
            <CardDescription>
              Delivery infrastructure is configured by platform admins. Tenant admins configure branding only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={status?.has_platform_email ? 'success' : 'warning'}>
                {status?.has_platform_email ? 'Configured' : 'Not Configured'}
              </Badge>
              <Badge variant={status?.is_platform_enabled ? 'success' : 'warning'}>
                {status?.is_platform_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge variant={status?.is_platform_healthy ? 'success' : 'warning'}>
                {status?.is_platform_healthy ? 'Healthy' : 'Unhealthy'}
              </Badge>
              <Badge variant="info">Provider: {status?.provider_type || 'n/a'}</Badge>
            </div>
            {!status?.has_platform_email || !status?.is_platform_enabled ? (
              <Alert variant="warning">
                Platform email is not available yet. Contact a platform admin to configure and enable delivery.
              </Alert>
            ) : null}
            <div className="grid gap-3 text-sm md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="text-gray-500 dark:text-gray-400">Effective From Name</div>
                <div className="mt-1 text-charcoal dark:text-gray-100">
                  {status?.effective_from_name || 'Not set'}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="text-gray-500 dark:text-gray-400">Effective From Email</div>
                <div className="mt-1 text-charcoal dark:text-gray-100">
                  {status?.effective_from_email || 'Not set'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenant Branding Overrides</CardTitle>
            <CardDescription>
              Optional sender/branding values used for tenant-specific invites, resets, and notifications.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  value={form.from_name}
                  onChange={(e) => setField('from_name', e.target.value)}
                  placeholder="Acme Inc"
                  disabled={!status?.has_platform_email}
                />
              </div>
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input
                  value={form.from_email}
                  onChange={(e) => setField('from_email', e.target.value)}
                  placeholder="noreply@acme.com"
                  disabled={!status?.has_platform_email}
                />
                {status?.verified_sender_domains?.length ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Allowed sender domains: {status.verified_sender_domains.join(', ')}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setField('company_name', e.target.value)}
                placeholder="Acme Inc"
                disabled={!status?.has_platform_email}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Shown in email branding header and default footer.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Reply-To</Label>
                <Input
                  value={form.reply_to}
                  onChange={(e) => setField('reply_to', e.target.value)}
                  placeholder="support@acme.com"
                  disabled={!status?.has_platform_email}
                />
              </div>
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <Input
                  value={form.primary_color}
                  onChange={(e) => setField('primary_color', e.target.value)}
                  placeholder="#c9506b"
                  disabled={!status?.has_platform_email}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setField('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                disabled={!status?.has_platform_email}
              />
            </div>
            <div className="space-y-2">
              <Label>Email Footer Text</Label>
              <Textarea
                value={form.email_footer_text}
                onChange={(e) => setField('email_footer_text', e.target.value)}
                placeholder="Need help? Contact support@acme.com"
                rows={3}
                disabled={!status?.has_platform_email}
              />
            </div>
            <div className="space-y-2">
              <Label>Powered By Footer</Label>
              <Select
                value={form.include_powered_by ? 'true' : 'false'}
                onValueChange={(value) => setField('include_powered_by', value === 'true')}
              >
                <SelectOption value="true">Show "Powered by Eliza Accelerate"</SelectOption>
                <SelectOption value="false">Hide powered-by line</SelectOption>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-gray-500" />
              Tenant Send Log
            </CardTitle>
            <CardDescription>Recent metadata-only sends for this tenant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectOption value="all">All statuses</SelectOption>
                <SelectOption value="sent">Sent</SelectOption>
                <SelectOption value="delivered">Delivered</SelectOption>
                <SelectOption value="failed">Failed</SelectOption>
                <SelectOption value="bounced">Bounced</SelectOption>
              </Select>
              <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                <SelectOption value="all">All purposes</SelectOption>
                <SelectOption value="invite">Invite</SelectOption>
                <SelectOption value="password_reset">Password Reset</SelectOption>
                <SelectOption value="notification">Notification</SelectOption>
                <SelectOption value="workflow">Workflow</SelectOption>
              </Select>
              <Button variant="outline" onClick={loadLogs} disabled={isRefreshingLogs}>
                {isRefreshingLogs ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="mr-2 h-4 w-4" />}
                Refresh
              </Button>
            </div>
            {logs.length === 0 ? (
              <Alert variant="info">No tenant send log entries found for current filters.</Alert>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-dark-surface-2">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Queued</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">To</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Purpose</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Error</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white dark:divide-dark-border dark:bg-dark-surface">
                    {logs.map((row) => {
                      const canResend =
                        row.status === 'failed' ||
                        row.status === 'bounced' ||
                        row.status === 'complaint';
                      return (
                        <tr key={row.id}>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            {new Date(row.queued_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-charcoal dark:text-gray-100">{row.to_email}</td>
                          <td className="px-3 py-2">
                            <Badge variant="default">{row.purpose}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            {row.status === 'delivered' || row.status === 'sent' ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircleIcon className="h-4 w-4" />
                                {row.status}
                              </span>
                            ) : row.status === 'failed' || row.status === 'bounced' ? (
                              <span className="inline-flex items-center gap-1 text-red-500">
                                <XCircleIcon className="h-4 w-4" />
                                {row.status}
                              </span>
                            ) : (
                              row.status
                            )}
                          </td>
                          <td className="max-w-[320px] truncate px-3 py-2 text-gray-500">
                            {row.error_message || '-'}
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!canResend || resendingLogId === row.id}
                              onClick={() => handleResend(row.id)}
                            >
                              {resendingLogId === row.id ? (
                                <Spinner size="sm" className="mr-2" />
                              ) : null}
                              Resend
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-xs text-gray-500 dark:text-gray-400">Total records: {totalLogs}</div>
          </CardContent>
        </Card>
      </PageBody>
    </Page>
  );
}
